import { cleanTranscript } from "@qn/agent-core";
import { Button, cn } from "@qn/ui";
import { Mic, MicOff, Type, Volume2, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useT } from "../lib/i18n";
import { usePrefs } from "../lib/prefs";
import {
  BrowserListener,
  browserLang,
  browserSTT,
  chunkForSpeech,
  createSpeaker,
  isIOS,
  openMic,
  Recorder,
  SentenceStream,
  type Speaker,
  transcribeServer,
  VAD,
  type VoiceSettings,
} from "../lib/voice";

type Phase = "listening" | "hearing" | "transcribing" | "thinking" | "speaking";

/**
 * Hands-free conversation: listen (VAD) → transcribe → send → speak the answer sentence by sentence.
 * Barge-in: if you start talking while it speaks, speech stops, the run is cancelled and it listens to you.
 */
export function VoiceMode({
  cfg,
  assistantText,
  assistantRunning,
  onSend,
  onStopRun,
  onClose,
  greeting,
  title,
  persona,
}: {
  cfg: VoiceSettings;
  assistantText: string;
  assistantRunning: boolean;
  onSend: (text: string) => void;
  onStopRun: () => void;
  onClose: () => void;
  /** Spoken first (e.g. the assistant's opening line of an in-app call), then the mic opens. */
  greeting?: string;
  title?: string;
  /** The assistant calling on someone's behalf: always a woman's voice, in the conversation's language. */
  persona?: { lang: string; gender?: "female" | "male" };
}) {
  const t = useT();
  const locale = usePrefs((s) => s.locale);
  const voiceReplies = usePrefs((s) => s.voiceReplies);
  const setPrefs = usePrefs((s) => s.set);
  const repliesRef = useRef(voiceReplies);
  repliesRef.current = voiceReplies;
  const [phase, setPhase] = useState<Phase>("listening");
  const [level, setLevel] = useState(0);
  const [heard, setHeard] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const speaker = useRef<Speaker | null>(null);
  const stream = useRef(new SentenceStream());
  const spoken = useRef(0); // characters of assistantText already queued
  const awaiting = useRef(false); // we sent something and expect an answer
  const vad = useRef<VAD | null>(null);
  const rec = useRef<Recorder | null>(null);
  const phaseRef = useRef<Phase>("listening");
  const mutedRef = useRef(false);
  const deafUntil = useRef(0); // ignore the mic until then (the room still rings after the assistant stops)
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const latest = useRef({ onSend, onStopRun, cfg, assistantRunning, assistantText });
  latest.current = { onSend, onStopRun, cfg, assistantRunning, assistantText };
  const setP = useCallback((p: Phase) => {
    phaseRef.current = p;
    setPhase(p);
  }, []);

  // ---- microphone + VAD session ----
  // biome-ignore lint/correctness/useExhaustiveDependencies: one mic session per mount
  useEffect(() => {
    let ms: MediaStream | null = null;
    let closed = false;
    const sp = createSpeaker(
      persona ? { ...cfg.tts, auto: true, gender: persona.gender ?? "female" } : cfg.tts,
      (issue) => setNote(t(`voice.issue.${issue}`)),
      persona?.lang === "ar" ? greeting : undefined,
      persona?.lang,
    );
    // The persona's language decides what we listen for too; languages the recogniser cannot be told about use server detection.
    const sttLang = (): "auto" | "ar" | "en" =>
      persona
        ? persona.lang === "ar"
          ? "ar"
          : persona.lang === "en"
            ? "en"
            : "auto"
        : latest.current.cfg.lang;
    const wantsBrowser = () =>
      latest.current.cfg.stt.source === "browser" &&
      !!browserSTT() &&
      !isIOS() &&
      !(persona && sttLang() === "auto");
    const browser = new BrowserListener();
    speaker.current = sp;
    (async () => {
      try {
        ms = await openMic();
        if (closed) {
          for (const x of ms.getTracks()) x.stop();
          return;
        }
        const v = new VAD(ms);
        const r = new Recorder(ms);
        vad.current = v;
        rec.current = r;
        v.onLevel = (x) => setLevel(Math.min(1, x * 12));
        v.onStart = () => {
          if (mutedRef.current) return;
          // Half-duplex: a phone microphone hears the assistant's own voice and would send it back as if the user said it.
          // So nothing is recorded while the assistant is thinking or talking (tap the circle to interrupt).
          if (phaseRef.current !== "listening" || sp.busy || Date.now() < deafUntil.current) return;
          // Always record: server transcription is the fallback if the browser recogniser is unavailable.
          r.start();
          if (wantsBrowser()) browser.start(browserLang(sttLang(), localeRef.current));
          setP("hearing");
        };
        v.onEnd = async () => {
          if (phaseRef.current !== "hearing") return;
          const useBrowser = wantsBrowser();
          const blob = await r.stop();
          if (blob.size < 1500) {
            if (useBrowser) browser.abort();
            return setP("listening"); // too short: a click or cough
          }
          setP("transcribing");
          try {
            let text = useBrowser ? cleanTranscript(await browser.stop()) : "";
            if (!text)
              text = cleanTranscript(await transcribeServer(blob, sttLang(), localeRef.current));
            if (!text) {
              setNote(t("voice.noSpeech"));
              return setP("listening");
            }
            setNote(null);
            setHeard(text);
            awaiting.current = true;
            stream.current = new SentenceStream();
            spoken.current = 0;
            setP("thinking");
            latest.current.onSend(text);
          } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
            setP("listening");
          }
        };
        v.run();
        if (greeting) {
          // The caller speaks first; the mic stays deaf until the greeting has finished.
          setP("speaking");
          for (const s of chunkForSpeech(greeting)) sp.speak(s);
          void sp.idle().then(() => {
            deafUntil.current = Date.now() + 900;
            if (phaseRef.current === "speaking") setP("listening");
            v.reset();
          });
        }
        // A dead microphone (permission granted but silent) looks exactly like "listening" — say so.
        setTimeout(() => {
          if (!closed && v.peak < 0.004) setNote(t("voice.noSignal"));
        }, 5000);
      } catch (e) {
        setError(e instanceof Error ? e.message : t("voice.noMic"));
      }
    })();
    return () => {
      closed = true;
      browser.abort();
      sp.cancel();
      vad.current?.close();
      for (const x of ms?.getTracks() ?? []) x.stop();
    };
  }, []);

  // ---- feed the assistant's streaming text to the speaker ----
  useEffect(() => {
    if (!awaiting.current) return;
    const sp = speaker.current;
    if (!sp) return;
    if (assistantText.length < spoken.current) spoken.current = 0; // a new agent step restarted the text
    const delta = assistantText.slice(spoken.current);
    if (delta) {
      spoken.current = assistantText.length;
      for (const s of stream.current.push(delta)) {
        if (repliesRef.current === "text") continue; // voice in, text out: show it, don't speak it
        sp.speak(s);
        if (phaseRef.current === "thinking") setP("speaking");
      }
    }
    if (!assistantRunning && assistantText.length >= 0) {
      for (const s of stream.current.flush()) {
        if (repliesRef.current === "text") continue;
        sp.speak(s);
        setP("speaking");
      }
      awaiting.current = false;
      void sp.idle().then(() => {
        deafUntil.current = Date.now() + 900;
        if (phaseRef.current === "speaking" || phaseRef.current === "thinking") setP("listening");
        vad.current?.reset();
      });
    }
  }, [assistantText, assistantRunning, setP]);

  // While the assistant talks, demand louder input so its own voice (speaker bleed) doesn't trigger barge-in.
  useEffect(() => {
    if (vad.current) vad.current.sensitivity = phase === "speaking" ? 6 : 3;
  }, [phase]);

  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    addEventListener("keydown", k);
    return () => removeEventListener("keydown", k);
  }, [onClose]);

  const label: Record<Phase, string> = {
    listening: t("voice.listening"),
    hearing: t("voice.hearing"),
    transcribing: t("voice.transcribing"),
    thinking: t("voice.thinking"),
    speaking: t("voice.speaking"),
  };
  const active = phase === "hearing" || phase === "speaking";
  const interrupt = () => {
    if (phaseRef.current !== "speaking" && phaseRef.current !== "thinking") return;
    speaker.current?.cancel();
    if (latest.current.assistantRunning) latest.current.onStopRun();
    awaiting.current = false;
    deafUntil.current = Date.now() + 500;
    vad.current?.reset();
    setP("listening");
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title ?? t("voice.title")}
      className="fixed inset-0 z-[70] flex flex-col items-center justify-between bg-[var(--bg)]/95 p-6 backdrop-blur-xl"
    >
      <div className="flex w-full max-w-md items-center justify-between">
        <span className="text-sm font-medium text-[var(--fg-muted)]">
          {title ?? t("voice.title")}
        </span>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label={t("artifact.close")}>
          <X className="size-5" />
        </Button>
      </div>
      <div className="flex flex-col items-center gap-8">
        <button
          type="button"
          onClick={interrupt}
          disabled={phase !== "speaking" && phase !== "thinking"}
          aria-label={phase === "speaking" || phase === "thinking" ? label[phase] : label.listening}
          className="relative grid size-56 place-items-center rounded-full disabled:cursor-default"
        >
          <div
            aria-hidden
            className={cn(
              "absolute inset-0 rounded-full bg-gradient-to-br from-[var(--accent)] to-[var(--accent-2)] opacity-20 blur-2xl transition-transform duration-100",
            )}
            style={{ transform: `scale(${active ? 1 + level * 0.9 : 0.85})` }}
          />
          <div
            aria-hidden
            className={cn(
              "absolute inset-6 rounded-full bg-gradient-to-br from-[var(--accent)] to-[var(--accent-2)] transition-transform duration-100",
              phase === "thinking" || phase === "transcribing" ? "animate-pulse" : "",
            )}
            style={{
              transform: `scale(${phase === "hearing" ? 1 + level * 0.35 : phase === "speaking" ? 1.04 + Math.sin(Date.now() / 150) * 0.02 : 1})`,
            }}
          />
          {muted ? (
            <MicOff className="relative size-10 text-[var(--accent-fg)]" />
          ) : (
            <Mic className="relative size-10 text-[var(--accent-fg)]" />
          )}
        </button>
        <div className="text-center">
          <div role="status" aria-live="polite" className="font-display text-2xl font-semibold">
            {muted ? t("voice.muted") : label[phase]}
          </div>
          {heard && (
            <p className="mx-auto mt-3 max-w-md text-sm text-[var(--fg-muted)]" dir="auto">
              “{heard}”
            </p>
          )}
          {note && !error && (
            <p
              role="status"
              className="mx-auto mt-3 max-w-md text-sm text-[var(--warn)]"
              dir="auto"
            >
              {note}
            </p>
          )}
          {assistantText &&
            (phase === "thinking" || phase === "speaking" || voiceReplies === "text") && (
              <p
                className="mx-auto mt-4 max-h-40 max-w-md overflow-y-auto text-start text-sm leading-relaxed"
                dir="auto"
              >
                {assistantText.slice(-600)}
              </p>
            )}
          {error && (
            <p
              role="alert"
              className="mx-auto mt-3 max-w-md text-sm text-[var(--danger)]"
              dir="auto"
            >
              {error}
            </p>
          )}
        </div>
      </div>
      <div className="flex gap-3">
        <Button
          variant="outline"
          size="lg"
          onClick={() => {
            mutedRef.current = !muted;
            setMuted(!muted);
          }}
        >
          {muted ? <Mic className="size-4" /> : <MicOff className="size-4" />}{" "}
          {muted ? t("voice.unmute") : t("voice.mute")}
        </Button>
        <Button
          variant="secondary"
          size="lg"
          aria-pressed={voiceReplies === "text"}
          onClick={() => {
            if (voiceReplies === "voice") {
              speaker.current?.cancel();
              setPrefs({ voiceReplies: "text" });
            } else setPrefs({ voiceReplies: "voice" });
          }}
        >
          {voiceReplies === "voice" ? <Volume2 className="size-4" /> : <Type className="size-4" />}{" "}
          {voiceReplies === "voice" ? t("voice.repliesVoice") : t("voice.repliesText")}
        </Button>
        <Button variant="danger" size="lg" onClick={onClose}>
          {t("voice.end")}
        </Button>
      </div>
    </div>
  );
}
